import React, { useEffect, useState } from 'react';
import { CircularProgress, Grid } from '@mui/material';
import { ChevronRight, EventOutlined, PlaceOutlined, ScheduleOutlined } from '@mui/icons-material';
import { getLocalEvents, LocalEvent } from 'core/API/local-events';
import './LocalEvents.scss';

interface LocalEventsProps {
	workplaceId?: number | null;
	className?: string;
	onEventClick?: (event: LocalEvent) => void;
	title?: string;
}

/**
 * Reusable component for displaying local events
 * Fetches events based on workplace ID
 */
export const LocalEvents: React.FC<LocalEventsProps> = ({
	workplaceId,
	className = '',
	onEventClick,
	title = 'Local Events',
}) => {
	const [localEvents, setLocalEvents] = useState<LocalEvent[]>([]);
	const [loading, setLoading] = useState(false);
	const isClickable = Boolean(onEventClick);

	useEffect(() => {
		const fetchLocalEvents = async () => {
			if (!workplaceId) return;

			setLoading(true);
			try {
				const events = await getLocalEvents(workplaceId);
				setLocalEvents(events);
			} catch (error) {
				console.error('Error fetching local events:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchLocalEvents();
	}, [workplaceId]);

	return (
		<Grid container direction="column" className={`local-events-container white-card ${className}`}>
			{loading ? (
				<Grid item className="loading-container">
					<CircularProgress size={20} />
				</Grid>
			) : (
				<Grid item className="local-events-list scrollable">
					{localEvents.map((event) => (
						<Grid
							key={event.id}
							container
							item
							className={`local-event-item${isClickable ? ' is-clickable' : ''}`}
							direction="row"
							wrap="nowrap"
							onClick={() => onEventClick?.(event)}
							role={isClickable ? 'button' : undefined}
							tabIndex={isClickable ? 0 : undefined}
							onKeyDown={(e) => {
								if (!isClickable) return;
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									onEventClick?.(event);
								}
							}}
						>
							<div className="event-icon-badge" aria-hidden>
								<EventOutlined fontSize="small" />
							</div>
							<div className="event-content">
								<div className="event-title">{event.title}</div>
								<div className="event-meta">
									<ScheduleOutlined className="meta-icon" fontSize="inherit" />
									<span>
										{event.date?.slice(0, 10)} {event.time?.slice(0, 5)} {event.timezone}
									</span>
								</div>
								{event.why_hot && <div className="why-hot">{event.why_hot}</div>}
								{(event.sport || event.broadcast) && (
									<div className="broadcast">{event.sport || event.broadcast}</div>
								)}
								<div className="event-location">
									<PlaceOutlined className="meta-icon" fontSize="inherit" />
									<span>
										{event.city}, {event.country}
									</span>
								</div>
							</div>
							{isClickable && (
								<div className="event-chevron" aria-hidden>
									<ChevronRight fontSize="small" />
								</div>
							)}
						</Grid>
					))}
					{localEvents.length === 0 && !loading && (
						<Grid item className="no-events">
							No local events available
						</Grid>
					)}
				</Grid>
			)}
		</Grid>
	);
};
